import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { useMemo } from "react";
import LoadingProvider from "./Context";
import Order from "./components/Order";
import Preview from "./components/Preview";
import TemplateOrder from "./components/TemplateOrder";
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import Layout from "./components/Layout"
import { AppContextProvider, useAppContext } from "./AppContext";
import { buildTheme, deriveColorsFromAccent } from "./theme";
import { domainDetails } from "./domainDetails";

const subdomain = window.location.host.split('.')[0];
const domainColors = domainDetails[subdomain]?.colors;

// Legacy theme — IDENTICAL config to prod (now built by theme.js); used for all fallback brands.
const theme = buildTheme(domainColors);

// DIY gate: brands with a LIVE config (brand.source === "api") render the self-contained template
// pages (Classic/Modern/Minimal via TemplateOrder); EVERYTHING ELSE falls back to the untouched
// legacy page (Layout + Order + module theme) — byte-identical to prod for all existing brands.
function ThemedRoutes() {
  const { brand, brandLoading } = useAppContext();
  const { pathname } = useLocation();
  const isConfigDriven = brand?.source === "api";
  const activeTheme = useMemo(
    () => (isConfigDriven ? buildTheme(deriveColorsFromAccent(brand?.bannerBgColor)) : theme),
    [isConfigDriven, brand]
  );
  // Known legacy brands (in the hardcoded map) render IMMEDIATELY — exactly prod's timing; their
  // config check resolves in the background (404 = nothing changes; a LIVE config switches them to
  // the template on arrival, which only happens mid-migration). Unknown subdomains wait for the
  // config check (one lambda call, bounded by its 4s timeout) so a DIY brand's page never flashes
  // the default UI first. /preview is exempt: it builds its brand from query params and needs
  // nothing from the config lookup.
  if (brandLoading && !domainDetails[subdomain] && !pathname.startsWith("/preview")) {
    return null;
  }
  return (
    <ThemeProvider theme={activeTheme}>
      <CssBaseline />
      <ToastContainer hideProgressBar position="bottom-center" theme="colored" closeButton={() => null} />
      <Routes>
        <Route path="/preview" element={<Preview />} />
        {isConfigDriven ? (
          <>
            <Route path="/:id" element={<TemplateOrder />} />
            <Route path="/" element={<TemplateOrder />} />
          </>
        ) : (
          <Route element={<Layout />}>
            <Route path="/:id" element={<Order />} />
            <Route path="/" element={<Order />} />
          </Route>
        )}
      </Routes>
    </ThemeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LoadingProvider>
        <AppContextProvider>
          <ThemedRoutes />
        </AppContextProvider>
      </LoadingProvider>
    </BrowserRouter>
  );
}

export default App;
